import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, MapPin, Clock, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { BUSINESS } from "@/lib/business";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name";
    if (!form.email.trim()) e.email = "Please enter your email";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Please enter a valid email";
    if (!form.message.trim()) e.message = "Please tell us about the job";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks — we have got your message.", {
        description: "We will be in touch by email shortly.",
      });
      setForm(initial);
    } catch (err) {
      toast.error("Could not send your message.", {
        description:
          err?.response?.data?.detail ||
          "Please try again or email us directly.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-white py-20 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="lg:col-span-5"
        >
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#64748B]">
            Get in touch
          </span>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold text-[#0A192F] tracking-tight">
            Tell us about the job — we will handle the rest.
          </h2>
          <p className="mt-6 text-[#334155] leading-relaxed">
            Send us a quick message and we will reply by email. Most quotes
            come back within the same working day.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <a
              href={BUSINESS.emailHref}
              data-testid="contact-cta-email"
              className="inline-flex items-center justify-center gap-2 bg-[#0A192F] text-white font-semibold px-6 py-4 rounded-md hover:bg-[#1E3A8A] transition-colors"
            >
              <Mail className="w-4 h-4" />
              Email Us
            </a>
            <a
              href="#contact-form"
              data-testid="contact-cta-form"
              className="inline-flex items-center justify-center gap-2 border-2 border-[#0A192F] text-[#0A192F] font-semibold px-6 py-4 rounded-md hover:bg-[#F8FAFC] transition-colors"
            >
              Contact Us Now
            </a>
          </div>

          <ul className="mt-12 space-y-6">
            <li className="flex items-start gap-4">
              <span className="inline-flex w-10 h-10 items-center justify-center rounded-md bg-[#0A192F] text-white">
                <Mail className="w-4 h-4" />
              </span>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#64748B]">
                  Email
                </p>
                <a
                  data-testid="contact-info-email"
                  href={BUSINESS.emailHref}
                  className="font-heading text-lg text-[#0A192F] font-semibold hover:underline break-all"
                >
                  {BUSINESS.email}
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span className="inline-flex w-10 h-10 items-center justify-center rounded-md bg-[#0A192F] text-white">
                <MapPin className="w-4 h-4" />
              </span>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#64748B]">
                  Service Area
                </p>
                <span className="font-heading text-lg text-[#0A192F] font-semibold">
                  {BUSINESS.area}
                </span>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span className="inline-flex w-10 h-10 items-center justify-center rounded-md bg-[#0A192F] text-white">
                <Clock className="w-4 h-4" />
              </span>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#64748B]">
                  Hours
                </p>
                <span className="text-[#0A192F] font-medium">
                  {BUSINESS.hours}
                </span>
              </div>
            </li>
          </ul>
        </motion.div>

        <motion.form
          id="contact-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          data-testid="contact-form"
          className="lg:col-span-7 bg-[#F8FAFC] border border-slate-200 rounded-md p-8 md:p-12"
          noValidate
        >
          <h3 className="font-heading text-xl sm:text-2xl font-semibold text-[#0A192F]">
            Contact Us Now
          </h3>
          <p className="text-sm text-[#64748B] mt-1">
            Fill in the form and we will get back to you by email.
          </p>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <Label htmlFor="name" className="text-[#0A192F]">
                Full Name
              </Label>
              <Input
                id="name"
                data-testid="contact-input-name"
                value={form.name}
                onChange={set("name")}
                placeholder="Jane Smith"
                className="mt-2 h-11 bg-white border-slate-300 focus-visible:ring-[#0A192F]"
              />
              {errors.name && (
                <p className="text-xs text-red-600 mt-1">{errors.name}</p>
              )}
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="email" className="text-[#0A192F]">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="contact-input-email"
                value={form.email}
                onChange={set("email")}
                placeholder="you@example.com"
                className="mt-2 h-11 bg-white border-slate-300 focus-visible:ring-[#0A192F]"
              />
              {errors.email && (
                <p className="text-xs text-red-600 mt-1">{errors.email}</p>
              )}
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="message" className="text-[#0A192F]">
                Message
              </Label>
              <Textarea
                id="message"
                data-testid="contact-input-message"
                value={form.message}
                onChange={set("message")}
                placeholder="Briefly describe the work you need..."
                rows={6}
                className="mt-2 bg-white border-slate-300 focus-visible:ring-[#0A192F]"
              />
              {errors.message && (
                <p className="text-xs text-red-600 mt-1">{errors.message}</p>
              )}
            </div>
          </div>

          <button
            type="submit"
            data-testid="contact-submit-button"
            disabled={submitting}
            className="mt-8 inline-flex items-center justify-center gap-2 bg-[#0A192F] text-white font-semibold px-6 py-4 rounded-md hover:bg-[#1E3A8A] disabled:opacity-60 disabled:cursor-not-allowed transition-colors w-full sm:w-auto"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" /> Send Enquiry
              </>
            )}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
